<template>
  <div>
    <!-- XOL-67 (B4): an RTA's Options table is read by `action_id` (GET /candidates/rta/{actionId}, 50 a page: API DEV-20).
         The legacy site tables (a Candidate site that kept its RTA, §2.6) still read by site and client. -->
    <el-alert
      v-if="loadFailed && isRta"
      class="rta-options-error"
      type="error"
      show-icon
      :closable="false"
      title="Couldn't load this RTA's options.">
      <el-button size="small" @click="retry">Retry</el-button>
    </el-alert>

    <xolas-table
      :title="title"
      :url="url"
      :table-ref="tableRef"
      :fields="fields"
      :options="options"
      :show-search="false"
      :hide-org-badge="isRta"
      :sort-field="isRta ? 'optionNo' : 'site_id,client_id,optionNo|desc'"
      :per-page="isRta ? 50 : 10"
      show-refresh
      :search-placeholder="searchPlaceholder"
      :layer-component="'candidate'"
      :layer-args="{ id: 'id' }"
      @total-changed="totalOptions"
      @loaded="onLoaded"
      @error="onLoadError">

      <!-- Add-Option button — XolasTable has no add-action prop (vuetable-2 legacy);
           render the action in the shared #actions slot like the other tables. -->
      <template v-if="!readOnly" #actions>
        <option-action :site-data="siteData" :total-options="currentNo" :rta-data="rtaData" :live-count="liveCount" />
      </template>

      <template #status="props">
        <el-tag size="small" effect="light" :type="tag(props.rowData).type" class="rta-opt-tag"><i :class="'fa ' + tag(props.rowData).icon" aria-hidden="true"></i>{{ props.rowData.optionStatus }}</el-tag>
        <div v-if="showChanged(props.rowData)" class="rta-opt-changed">
          <el-tag size="mini" effect="light" type="warning" class="rta-chip"><i class="fa fa-pencil-alt" aria-hidden="true"></i>Changed since submitted</el-tag>
        </div>
      </template>

      <template #option="props">

            <!-- XOL-67 (V4, decisions 144, 155): an RTA's option wears the pin its number gives on every map (rtaOption.optionMarkerLook: Option 1 green, then
                 blue, yellow, red, black, grey, Google, the outline pin from 8), so the row and the map agree. It follows the CURRENT number. Decorative:
                 the number is written. -->
            <img v-if="isRta" class="rta-opt-pin" :src="pinOf(props.rowData).icon" alt="" />
            <!-- eslint-disable-next-line vue/no-v-html -- formatter output, numeric → <b><sup> ordinal markup -->
            <span v-html="addSuffix(props.rowData.optionNo)" />
            &nbsp;{{props.rowData.title ? props.rowData.title + (props.rowData.clientRef ? ' | Ref:' + props.rowData.clientRef : '') : props.rowData.clientRef}}</b>

            <span v-if="props.rowData.surveyDate">&nbsp;Survey Date: {{formatDate(props.rowData.surveyDate)}}</span>
            <span v-if="props.rowData.landType">&nbsp;|&nbsp;Land Type: <b>{{props.rowData.landType}}</b></span>
            <span v-if="props.rowData.classificationType">&nbsp;|&nbsp;Classification Type: <b>{{props.rowData.classificationType}}</b></span>

      </template>


      <template #details="props">
        <span label="Structure Type" v-if="props.rowData.structure"><i class="fa fa-broadcast-tower">&nbsp;</i>{{props.rowData.structure.name}}&nbsp;<b v-if="props.rowData.structureHeight">{{props.rowData.structureHeight}}m.</b>&nbsp;</span>
        <!-- Decision 185: 0 is a value ("Ant. 0m"); only an empty height has no label. -->
        <span v-if="props.rowData.antennaHeight !== null && props.rowData.antennaHeight !== undefined && props.rowData.antennaHeight !== ''">Ant. <i class="fa fa-arrows-alt-v"></i> <b>{{props.rowData.antennaHeight}}m</b></span>
      </template>


      <template #distance="props">

          <el-tooltip v-if="props.rowData.distanceFromNominal" content="Distance from Nominal"  placement="left">
             <span> <i class="fa fa-bullseye"></i> <b>{{props.rowData.distanceFromNominal}}m</b></span>
          </el-tooltip>

          <el-tooltip v-if="props.rowData.distanceAccessRoad" content="Distance to Access Road" placement="top">
             <span>&nbsp;|&nbsp;<i class="fa fa-road"></i> <b>{{props.rowData.distanceAccessRoad}}m</b></span>
          </el-tooltip>

          <el-tooltip v-if="props.rowData.distanceNearestBuilding" content="Distance to Nearest Building">
             <span>&nbsp;|&nbsp;<i class="fa fa-home"></i> <b>{{props.rowData.distanceNearestBuilding}}m</b></span>
          </el-tooltip>

          <el-tooltip v-if="props.rowData.distanceNearestPower" content="Distance to Nearest Commerical Power" placement="right">
            <span>&nbsp;|&nbsp;<i class="fa fa-plug"></i> <b>{{props.rowData.distanceNearestPower}}m</b></span>
          </el-tooltip>

      </template>


      <template #delete="props">
        <el-button
          v-if="canDelete(props.rowData)"
          type="danger"
          circle
          icon="fa fa-trash"
          size="mini"
          :aria-label="'Delete option ' + props.rowData.optionNo"
          @click="deleteOption(props.rowData)" />
        <!-- Decision 122 (Carlos): the client's own option that has moved on past Draft keeps its trash button, disabled, with the
             reason. A disabled button fires no mouse events, so the tooltip is on the wrapper, which takes focus (as RtaDecision's
             e-mail button does). The options the Tower Co added get no button. -->
        <el-tooltip v-else-if="deleteBlockedText(props.rowData)" :content="deleteBlockedText(props.rowData)" placement="top" effect="dark">
          <span
            class="rta-delete-blocked"
            tabindex="0"
            role="button"
            aria-disabled="true"
            :aria-label="'Delete option ' + props.rowData.optionNo + ' is not available. ' + deleteBlockedText(props.rowData)">
            <el-button
              type="danger"
              circle
              disabled
              icon="fa fa-trash"
              size="mini"
              aria-hidden="true"
              tabindex="-1" />
          </span>
        </el-tooltip>
      </template>

      <!-- Not after a failed read: "No options yet" would say something the server never did. A slot with nothing in it falls back to
           XolasTable's own "No records", which says the same, so the failed read draws a blank instead. -->
      <template v-if="isRta" #empty>
        <span v-if="!loadFailed" class="text-muted rta-options-empty">{{ emptyText }}</span>
        <span v-else class="rta-options-unread">&nbsp;</span>
      </template>

    </xolas-table>
    <center v-if="!(isRta && loadFailed)" class="text-muted">Total: {{currentNo}}</center>

    </div>
</template>


<script>

import SiteMixins from '@/mixins/SiteMixins'
import RtaCommandMixins from '@/mixins/RtaCommandMixins'

import * as rtaStage from '@/lib/rtaStage'
import * as rtaOption from '@/lib/rtaOption'

import OptionAction from '@/components/actions/OptionAction'
import XolasTable from '@/components/atoms/tables/XolasTable'

export default {
    mixins: [SiteMixins, RtaCommandMixins],

    components: {
      XolasTable,
      OptionAction
    },

    props: {

      siteData: {
        type: Object,
        required: true
      },

      siteId: {
        type: Number,
        default: null
      },

      // XOL-67 (B4): the RTA whose options these are (read by action_id), and the RTA itself (a full action) for the gates.
      actionId: {
        type: Number,
        default: null
      },

      rtaData: {
        type: Object,
        default: null
      },

      sharerId: {
        type: Number,
        default: null
      },

      title: {
        type: String,
        default: 'Options'
      },

      noAdd: {
        type: Boolean,
        default: false
      },

      hideTop: {
        type: Boolean,
        default: true
      },

      showSearch: {
        type: Boolean,
        default: true
      },

      showRefresh: {
        type: Boolean,
        default: true
      },

      searchPlaceholder: {
        type: String,
        default: 'Client Ref, Land Type, Station Type...'

      },

      readOnly: {
        type: Boolean,
        default: false
      }


    },


    computed: {

      isRta () {
        return !!this.actionId
      },

      url () {
        if (this.isRta) {
          return '/candidates/rta/' + this.actionId
        }

        return '/candidates' + (this.siteId ? '/site/' + this.siteId : '') + (this.clientId ? '/client/' + this.clientId : '')
      },

      // The store, as computed (not data()): it hydrates after the first paint.
      viewerOrg () {
        return this.org()
      },

      viewerProfile () {
        return this.profile()
      },

      rtaCtx () {
        return rtaStage.rtaOf(this.rtaData)
      },

      // The RTA's live options (the limit of 5): the server's count, else the rows on screen.
      liveCount () {
        var live = this.rtaData ? Number(this.rtaData.liveOptionCount) : NaN

        return isNaN(live) ? rtaStage.liveOptionCount(this.rows) : live
      },

      // A normal Searching state, right after create or after every option is deleted (UX spec 2.3, M-4b): either side can add (the client and a Tower Co
      // responder), so the line points at the opener. Nothing to point at where the opener is not drawn (a record seen from elsewhere).
      emptyText () {
        return this.readOnly
          ? 'No options yet.'
          : 'No options yet. Add the first one with + ' + this.addSuffix(1, false) + ' Option.'
      },

      // Legacy tables: the client of the options listed (a Telco's own org, else the sharer asked for).
      clientId () {
        return this.isTelco() ? Number(this.org().id) : this.sharerId
      }

    },


    methods: {

      tag (row) {
        return rtaStage.optionStatusMeta(row.optionStatus)
      },

      // The option's pin by its number (the same look the maps draw).
      pinOf (row) {
        return rtaOption.optionMarkerLook(row.optionNo)
      },

      // "Changed since submitted" is the Tower Co's cue (never the client's): the SERVER's flag (API DEV-9, DEV-24).
      showChanged (row) {
        return this.isRta && this.isTowerCo() && rtaStage.responseChanged(row)
      },

      canDelete (row) {
        if (this.readOnly) {
          return false
        }

        if (this.isRta) {
          return rtaStage.canDeleteOption(row, this.rtaCtx, this.viewerOrg, this.viewerProfile)
        }

        return this.isTelco() || this.isManager('project|property|finance|admin|management')
      },

      // Why this row's trash button is disabled (decision 122): the tooltip, or '' when it is enabled or not drawn.
      deleteBlockedText (row) {
        if (!this.isRta || this.readOnly) {
          return ''
        }

        return rtaOption.deleteBlockedCopy(rtaStage.deleteBlockedReason(row, this.rtaCtx, this.viewerOrg, this.viewerProfile), { towerCo: this.towerCoName() })
      },

      deleteOption (row) {
        if (!this.isRta) {
          this.deleteItem(this, row.id, '/candidate', this.addSuffix(row.optionNo) + 'Option [' + row.site.siteName + ']', null, true, this.tableRef)
          return Promise.resolve(null)
        }

        var self = this
        var copy = rtaOption.confirmCopy('delete', {}, { n: row.optionNo })

        return this.$confirm(copy.message, copy.title, {
          confirmButtonText: copy.confirmButtonText,
          cancelButtonText: copy.cancelButtonText,
          type: copy.type,
          center: true
        }).then(function () {
          return self.rtaCommand('delete', '/candidate/' + row.id, undefined, 'Option ' + row.optionNo).then(function (result) {
            if (!result.ok) {
              self.rtaRefuse(result, { telco: self.clientName(), towerCo: self.towerCoName(), role: self.isTowerCo() ? 'tcc' : 'client', optionNo: row.optionNo, command: 'delete' })
              self.$events.emit(self.tableRef + '-refresh-table')
              self.rtaAnnounce(null, self.actionId)
              return result
            }

            self.$message({ message: rtaOption.sentence('delete', {}, { n: row.optionNo }), type: 'success' })
            self.$events.emit(self.tableRef + '-refresh-table')
            self.rtaAnnounce(result.data, self.actionId)

            return result
          })
        }).catch(function () {
          return null
        })
      },

      clientName () {
        var c = this.rtaData && this.rtaData.client

        return (c && (c.name || c.shortName)) || 'the client'
      },

      totalOptions (no) {
        this.currentNo = no
      },

      onLoaded (rows) {
        this.rows = rows || []
        this.loadFailed = false
        this.$emit('loaded', this.rows)
      },

      onLoadError () {
        this.loadFailed = true
      },

      retry () {
        this.$events.emit(this.tableRef + '-refresh-table')
      }

    },


    data () {

      return {
        tableRef: 'siteCandidatesTable',

        // An RTA starts from its own count of options (the flat optionCount, DEV-8; never the site's count) until the table reports its
        // total; the table's total-changed then replaces it after every read, so the + Option label (count + 1, decision 133) follows an
        // add or a delete.
        currentNo: this.actionId
          ? (Number(this.rtaData && this.rtaData.optionCount) || 0)
          : (this.siteData.count ? this.siteData.count : 0),

        // The rows on screen, and whether the last read failed.
        rows: [],
        loadFailed: false,

        options: {
          siteData: this.siteData
        },

        // Per-column min-widths in the legacy vuetable's content ratios (Option is
        // the dominant column; delete is just the trash button) so el-table flexes
        // them to fill the table instead of distributing every column equally. Kept
        // proportional and summing under the table width so the columns fill without
        // a horizontal scrollbar. Client Status is wide enough to keep "Approved" on
        // one line — XOL-231. Details is 170, not 186, and Distance 150, not 184 (V8 fixes): the
        // delete column now shows at every width, and the columns must fit the drawer's table or the trash is scrolled out of sight: at a 1024 window it is
        // about 770px (70 + 190 + 300 + 150 + 50 = 760), at 1200 about 930px (adding Details: 930).
        fields: [
          this.actionId
            ? { name: '__slot:status', title: 'Status', minWidth: '190' }
            : { name: 'clientStatus', title: 'Client Status', callback: this.formatStatus, minWidth: '110' },
          {
            name: '__slot:option',
            title: 'Option',
            minWidth: '300'
          },
          {
            name: '__slot:details',
            titleClass: 'hidden-md-and-down',
            dataClass: 'hidden-md-and-down',
            minWidth: '170'
          },
          {
            name: '__slot:distance',
            titleClass: 'hidden-sm-and-down',
            dataClass: 'hidden-sm-and-down',
            minWidth: '150'
          },
          {
            // No `hidden-*` class: this is the only way to delete (or see why one cannot), so it shows at every width the table renders at
            // (XolasTable drops a column at the breakpoint its class names; V8 fixes).
            name: '__slot:delete',
            width: '50'
          }
        ]

      }

  },  // data


  mounted () {

    if (!this.telcoId && this.isTelco()) {
      this.telcoId = Number(this.org().id)
    }

  }

}

</script>

<style scoped>
  .rta-opt-pin { height: 20px; width: auto; vertical-align: middle; margin: -3px 6px -3px 0; }
  .rta-delete-blocked { display: inline-block; cursor: not-allowed; }
  .rta-delete-blocked >>> .el-button { cursor: not-allowed; }
</style>
